import {
  contentPageKindSchema,
  notShownReasonSchema,
  productSourceStateSchema,
  productStatusSchema,
  type StoreCapability,
} from '@avrahami/contracts';
import { recordPage } from '@avrahami/ui';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { z } from 'zod';
import { AppLayout, RequireCapability } from './components/Layout';
import { basepath } from './lib/store-path';
import { AccountPage } from './pages/AccountPage';
import { ActivityPage } from './pages/ActivityPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { BrandingPage } from './pages/BrandingPage';
import { CategoriesPage } from './pages/CategoriesPage';
import { CategoryEditPage, CategoryNewPage } from './pages/CategoryEditPage';
import { ContentPageEditPage, ContentPageNewPage } from './pages/ContentPageEditPage';
import { ContentPagesPage } from './pages/ContentPagesPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { CustomersPage } from './pages/CustomersPage';
import { DashboardPage } from './pages/DashboardPage';
import { EnterPage } from './pages/EnterPage';
import { HomePage } from './pages/HomePage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { InvitationPage } from './pages/InvitationPage';
import { LoginPage } from './pages/LoginPage';
import { NoStorePage } from './pages/NoStorePage';
import { PopupsPage } from './pages/PopupsPage';
import { ProductCreatePage } from './pages/ProductCreatePage';
import { ProductEditPage } from './pages/ProductEditPage';
import { ProductPullPage } from './pages/ProductPullPage';
import { ProductsPage } from './pages/ProductsPage';
import { QuoteRequestDetailPage } from './pages/QuoteRequestDetailPage';
import { QuoteRequestsPage } from './pages/QuoteRequestsPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SettingsPage } from './pages/SettingsPage';
import { StaffPage } from './pages/StaffPage';
import { SupportPage } from './pages/SupportPage';

/** A screen that explains itself to a role without the capabilities it needs. */
function guarded(needs: readonly StoreCapability[], Page: ComponentType) {
  return function GuardedPage() {
    return (
      <RequireCapability needs={needs}>
        <Page />
      </RequireCapability>
    );
  };
}

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: Outlet,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: z.object({ redirect: z.string().max(2048).optional() }),
  component: LoginPage,
});

const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitation',
  component: InvitationPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

/** The mailed "forgot password" link opens here (adr/0035). */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

/** Platform staff arrive here with the entry token of the store they manage (adr/0031). */
const enterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/enter',
  component: EnterPage,
});

const noStoreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/no-store',
  component: NoStorePage,
});

/** Everything behind sign-in and an active store (checked in AppLayout). */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: guarded(['store.settings.read', 'store.orders.read'], DashboardPage),
});

/** Quote requests (adr/0022); the API and the capabilities keep calling them orders. */
const quoteRequestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quote-requests',
  component: guarded(['store.orders.read'], QuoteRequestsPage),
});

const quoteRequestRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quote-requests/$orderId',
  component: guarded(['store.orders.read'], QuoteRequestDetailPage),
});

const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers',
  component: guarded(['store.customers.read'], CustomersPage),
});

const customerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers/$customerId',
  component: guarded(['store.customers.read'], CustomerDetailPage),
});

const productsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/products',
  // Filters a link may open the list with (the pull screen links to drafts, discontinued and
  // archived products; the dashboard to the products not shown in the store). Anything else in
  // the address is ignored.
  validateSearch: z.object({
    status: productStatusSchema.optional().catch(undefined),
    sourceState: productSourceStateSchema.optional().catch(undefined),
    notShown: notShownReasonSchema.optional().catch(undefined),
  }),
  component: guarded(['store.products.read'], ProductsPage),
});

const productCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/products/new',
  component: guarded(['store.products.write'], ProductCreatePage),
});

/** A fixed path: it wins over `/products/$productId` whatever the order of the routes. */
const productPullRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/products/pull',
  component: guarded(['store.sync.run'], ProductPullPage),
});

/**
 * The copy screen moved to the platform (adr/0023). An old link to it opens the product list
 * instead of being read as a product id.
 */
const retiredCopyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/products/copy',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- the router's redirect
    throw redirect({ to: '/products' });
  },
});

const productRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/products/$productId',
  component: guarded(['store.products.read'], ProductEditPage),
});

const categoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/categories',
  component: guarded(['store.products.read'], CategoriesPage),
});

/** A fixed path: it wins over `/categories/$categoryId` whatever the order of the routes. */
const categoryCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/categories/new',
  component: guarded(['store.products.write'], CategoryNewPage),
});

const categoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/categories/$categoryId',
  component: guarded(['store.products.write'], CategoryEditPage),
});

const popupsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/popups',
  component: guarded(['store.promotions.read'], PopupsPage),
});

const brandingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/branding',
  component: guarded(['store.settings.read'], BrandingPage),
});

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/home',
  component: guarded(['store.settings.read'], HomePage),
});

const pagesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pages',
  component: guarded(['store.settings.read'], ContentPagesPage),
});

const pageCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pages/new',
  validateSearch: z.object({ kind: contentPageKindSchema.catch('CUSTOM') }),
  component: guarded(['store.settings.write'], ContentPageNewPage),
});

const pageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pages/$pageId',
  component: guarded(['store.settings.read'], ContentPageEditPage),
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: guarded(['store.settings.read'], SettingsPage),
});

/**
 * The domain screen moved to the platform (adr/0025). An old link to it opens the dashboard.
 */
const retiredDomainsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/domains',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- the router's redirect
    throw redirect({ to: '/' });
  },
});

const analyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/analytics',
  component: guarded(['store.settings.read'], AnalyticsPage),
});

const staffRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/staff',
  component: guarded(['store.staff.read'], StaffPage),
});

const activityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/activity',
  component: guarded(['store.audit.read'], ActivityPage),
});

/** The store's service calls to the platform (adr/0036); a link may open it on one call. */
const supportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/support',
  validateSearch: z.object({ ticket: z.uuid().optional().catch(undefined) }),
  component: guarded(['store.support.use'], SupportPage),
});

const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account',
  component: AccountPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  invitationRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  noStoreRoute,
  enterRoute,
  appRoute.addChildren([
    dashboardRoute,
    quoteRequestsRoute,
    quoteRequestRoute,
    customersRoute,
    customerRoute,
    productsRoute,
    productCreateRoute,
    productPullRoute,
    retiredCopyRoute,
    productRoute,
    categoriesRoute,
    categoryCreateRoute,
    categoryRoute,
    popupsRoute,
    brandingRoute,
    homeRoute,
    pagesRoute,
    pageCreateRoute,
    pageRoute,
    settingsRoute,
    retiredDomainsRoute,
    analyticsRoute,
    staffRoute,
    activityRoute,
    supportRoute,
    accountRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  const router = createRouter({
    routeTree,
    // A tab of a store works under its address (adr/0033); its links stay the store's own paths.
    basepath,
    context: { queryClient },
    defaultPreload: 'intent',
    // Going back returns to the same place: the scroll position of every history entry.
    scrollRestoration: true,
  });
  // "Back to the list" links go back in history when the list is where the person came from.
  router.subscribe('onResolved', recordPage);
  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
